import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './CheckoutForm';
import MultibancoDetails from './MultibancoDetails';
import { decodePaymentToken } from '../utils/paymentToken';
import './LandingPage.css';

// Página de checkout servida no domínio do corp (ex: pay.reactstep.pro).
// Recebe o client_secret/publishable_key no fragment (#cs=...&pk=...),
// colocados lá pelo redirect da LandingPage (fluxo bot) ou pelo gateway (WooCommerce).
//
// Antes de montar o Stripe Elements, valida:
//   1) o estado real do intent no Stripe (pago/cancelado -> não remonta o checkout)
//   2) para tokens do bot, as nossas regras na API (MB já gerado, payment apagado, expirado)
export default function CheckoutPage() {
  const { token } = useParams();

  const { clientSecret, publishableKey, returnUrl, provider, intentId } = useMemo(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    return {
      clientSecret: params.get('cs') || '',
      publishableKey: params.get('pk') || '',
      // Para onde voltar depois de pagar: a página "order received" da loja
      // (WooCommerce) ou a nossa /success com a referência opaca do gateway.
      returnUrl: params.get('ru') || params.get('su') || '',
      // Processador e id do intent: a Airwallex precisa dos dois para montar
      // o Drop-in. Sem pv, é Stripe — é como os links antigos vêm.
      provider: params.get('pv') || 'stripe',
      intentId: params.get('pi') || '',
    };
  }, []);

  // Valor e dados do cliente vêm do token (mesmo formato da LandingPage)
  const tokenData = useMemo(() => {
    try {
      return decodePaymentToken(token);
    } catch (err) {
      return null;
    }
  }, [token]);

  const stripePromise = useMemo(
    () => (provider === 'stripe' && publishableKey ? loadStripe(publishableKey) : null),
    [provider, publishableKey]
  );

  // 'checking' -> a validar | 'ready' -> monta o checkout
  // 'done' -> pagamento já recebido | 'unavailable' -> nada para ver
  const [status, setStatus] = useState('checking');
  const [mbDetails, setMbDetails] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      if (!clientSecret || !publishableKey || !tokenData?.value) {
        setStatus('unavailable');
        return;
      }

      if (provider === 'airwallex' && !intentId) {
        setStatus('unavailable');
        return;
      }

      try {
        // 1) Estado real do intent no processador. Só o Stripe permite lê-lo a
        //    partir do browser; na Airwallex o SDK não expõe equivalente, por
        //    isso a validação fica pelas nossas regras (passo 2) e pelo próprio
        //    processador, que recusa pagar um intent já pago.
        if (provider === 'stripe') {
          const stripe = await stripePromise;
          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

          if (cancelled) return;

          if (!paymentIntent || paymentIntent.status === 'canceled') {
            setStatus('unavailable');
            return;
          }

          if (
            paymentIntent.status === 'succeeded' ||
            paymentIntent.status === 'processing'
          ) {
            setStatus('done');
            return;
          }
        }

        // 2) Tokens do bot: aplicar também as nossas regras (a chamada vai por /api,
        //    mesma origem — o browser continua sem ver o domínio central)
        if (tokenData.username && tokenData.source !== 'woocommerce') {
          try {
            // Com timeout: sem ele, uma API que não responda deixa a página
            // presa em "A preparar o pagamento..." para sempre. O comentário
            // abaixo já dizia que se deixa montar em caso de falha — é este
            // limite que faz isso acontecer de facto.
            const { data } = await axios.post(
              `${process.env.REACT_APP_API_URL}/v2/verify-token`,
              { token },
              { timeout: 8000 }
            );

            if (cancelled) return;

            if (data.multibanco) {
              // Referência MB já atribuída: mostrar os dados, não o checkout
              setMbDetails(data.multibanco);
              return;
            }

            if (data.payment_link) {
              window.location.replace(data.payment_link);
              return;
            }

            if (data.isValid === false) {
              // Payment renovado/apagado/expirado
              setStatus('unavailable');
              return;
            }
          } catch (err) {
            // API indisponível: deixamos montar — o processador continua a
            // impedir pagamentos duplicados no mesmo intent
            console.warn('Não foi possível validar o token na API');
          }
        }

        setStatus('ready');
      } catch (err) {
        console.error('Erro ao validar o pagamento');
        if (!cancelled) setStatus('unavailable');
      }
    };

    validate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mbDetails) {
    return <MultibancoDetails details={mbDetails} />;
  }

  if (status === 'checking') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">A preparar o pagamento...</h2>
          <p className="messageText">Aguarde um momento.</p>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Pagamento enviado!</h2>
          <p className="messageText">
            O seu pagamento já foi recebido e está a ser processado.
            Em breve será notificado. Se precisar de suporte, entre em contato.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Não há nada para ver aqui!!</h2>
          <p className="messageText">Contacte o administrador</p>
        </div>
      </div>
    );
  }

  return (
    <CheckoutForm
      provider={provider}
      stripePromise={stripePromise}
      publishableKey={publishableKey}
      intentId={intentId}
      clientSecret={clientSecret}
      token={token}
      email={tokenData.email || ''}
      fullName={`${tokenData.firstName || ''} ${tokenData.lastName || ''}`.trim()}
      value={tokenData.value}
      username={tokenData.username}
      packageName={tokenData.package}
      returnUrl={returnUrl}
    />
  );
}
